<template>
        <div>
            <alert-box v-if="!hasOptions">
            <template #body>
                This symbol does not have options associated with it. Please choose another.
            </template>
        </alert-box>

        <alert-box v-if="hasOptions && error.noOptions">
            <template #body>
                Options are not available for the desired contract month. Please choose another.
            </template>
        </alert-box>

        <div class="border-t-2 border-blue-600 mt-2 md:mt-4 print:hidden">
            <div class="tan-control-box">
                <div class="select-title pt-1 pb-1 print:hidden">Select:</div>
                <div class="print:w-full">
                    <option-type-selector
                        v-model="modelOption"
                        @update-description="updateDescription"
                        @error="setError"
                        :initial-symbol="initialSymbol"
                        :groups="groups"
                    ></option-type-selector>
                </div>
                <div v-if="modelOption" @click="navigate" class="btn btn-sm btn-yellow mb-3 print:hidden">Show Option Quotes</div>
            </div>
        </div>

        <div v-if="optionSelected && hasOptions && !error.noOptions" class="my-4">
            <option-info-block :loading="loadingQuotes" :mini-chart="rMiniChart" :quotes="rQuotes"></option-info-block>
            <div class="xs:flex-col table-header border-t-2">
                <span class="print:w-full"><strong>{{ description }}</strong></span>
                <div class="ml-auto flex print:hidden">
                    <dt-request-select
                        class="w-full sm:w-auto pr-3 xs:pr-0 xs:pb-2"
                        v-model="modelView"
                        label="View:"
                        :options="views"
                    ></dt-request-select>
                    <!-- <div class="xs:w-full w-full sm:w-auto pr-3 xs:pr-0 xs:pb-2">
                        <div class="relative xs:w-full">
                            <label class="leading-none self-center text-black mr-2" for="select-view">View:</label>
                            <select class="form-control form-control-sm form-control-fixed bg-white mb-1"
                                v-model="modelView"
                                @change="handleChange"
                                id="select-view"
                                aria-label="Select View"
                            >
                                <template v-for="option in views">
                                    <option :class="{ 'bg-yellow-200': option.highlight }" :value="option.value">
                                        {{ option.label }}
                                    </option>
                                </template>
                            </select>
                        </div>
                    </div> -->
                    <!-- <div class="relative w-full sm:w-auto">
                        <label for="timeframePeriod" class="leading-none self-center text-black mr-2">Period:</label>
                        <select id="timeframePeriod"
                            class="form-control form-control-sm form-control-fixed bg-white mb-1"
                            v-model="modelTimeframe"
                            aria-label="Select Period"
                        >
                            <option v-for="option in timeframes" :key="option.value" :class="{ 'bg-yellow-200': option.highlight }" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div> -->
                    <dt-timeframe-select class="w-full sm:w-auto" v-model="modelTimeframe" :timeframes="timeframes" :default-fields="request.fields"></dt-timeframe-select>
                </div>
            </div>

            <data-table :default-columns="modelView.columns" :default-labels="modelView.labels" :default-request="request" :scroll="false">
            </data-table>
        </div>

    </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';
import { useHighlightAtTheMoney } from '../mixins/HighlightAtTheMoney';

const props = defineProps({
    groups: {
        type: Object,
        required: true
    },
    views: {
        type: Array,
        required: true
    },
    hasOptions: Boolean,
    initialSymbol: Object,
    modelValue: {
        type: [String, Object],
        default: null, // You can set a default value if necessary
    },
});

const emit = defineEmits();

const defaultTimeframes = [
    { label: 'Intraday', value: 'intraday' },
    { label: 'End-of-Day', value: 'eod' },
];

const { highlightAtTheMoney } = useHighlightAtTheMoney(); // Assuming you've refactored HighlightAtTheMoney into a composable.

const defaultViews = ref(props.views);
const modelView = ref(props.views.find(mView => mView.default).value);
const loadingQuotes = ref(false);
const optionSelected = ref(false);
const request = ref({});
const rQuotes = ref({});
const rMiniChart = ref({});
const timeframes = ref(null);
const modelTimeframe = ref(null);
const modelOption = ref(null);
const description = ref(null);
const newDescription = ref(null);
const error = ref({ noOptions: false });
const initialLoad = ref(false);

const navigate = () => {
    const route = window.route('options.quotes', {
        symbol: modelOption.value.underlyingFuture,
        option: modelOption.value.symbol
    });
    window.location = route;
};

const setError = () => {
    error.value.noOptions = true;
};

const updateMiniChart = () => {
    rMiniChart.value = {
        symbol: modelOption.value.underlyingFuture,
        title: `${rQuotes.value.symbolName} ${rQuotes.value.contractMonthYear} Daily Chart`,
    };
};

const updateDescription = (description) => {
    newDescription.value = description;
};

const highlightRows = (rows) => {
    if (typeof rQuotes.value.raw === 'undefined') {
        return rows;
    }
    return highlightAtTheMoney(rQuotes.value.raw.lastPrice, rows);
};

const getQuotes = () => {
    if (modelOption.value.symbol && modelOption.value.underlyingFuture) {
        const route = window.route('api.quotes');

        request.value = Object.assign({}, modelView.value.request, {
            symbol: modelOption.value.symbol,
            underlying: modelOption.value.underlyingFuture,
            afterFunc: highlightRows,
        });

        description.value = newDescription.value;
        optionSelected.value = true;
        loadingQuotes.value = true;

        const params = {
            symbol: modelOption.value.symbol,
            fields: 'symbol,symbolName,daysToExpiration,optionsExpiration,lastPrice,pointValue,impliedVolatility,contractMonthYear',
            method: 'futuresOptions'
        };

        axios.post(route, params)
            .then((response) => {
                rQuotes.value = response.data.data[0];
                updateMiniChart();
            }).catch((error) => {
                console.error(error);
            }).then(() => {
                loadingQuotes.value = false;
            });
    }
};

// watch(modelView, (newView) => {
watch(() => modelView.value, (newView) => {
    if (newView) {
        const viewObj = props.views.find((mView) => {
            // Compare based on some unique property within request. Adjust as necessary.
            return JSON.stringify(mView.value.request) === JSON.stringify(newView.request);
        });

        if (viewObj.label === 'Analysis' || viewObj.label === 'Calculator') {
            // Only EOD
            timeframes.value = [defaultTimeframes[1]];
            modelTimeframe.value = defaultTimeframes[1].value;
        } else {
            timeframes.value = defaultTimeframes;
        }
        request.value = Object.assign({}, newView.request, {
            symbol: modelOption.value.symbol,
            underlying: modelOption.value.underlyingFuture,
            afterFunc: highlightRows,
        });
    }
});

watch(() => modelOption.value, (newOption, oldOption) => {
    if (initialLoad.value) {
        // We're attempting to load the table on the initial page load
        if (newOption != oldOption) {
            getQuotes();
            initialLoad.value = false;
        }
    }
});

onMounted(() => {
    initialLoad.value = true;
    timeframes.value = defaultTimeframes;
});
</script>

<style lang="scss">
#mini-chart {
    * {
        font-family: Roboto, sans-serif !important;
    }

    @screen lg {
        height: 9rem;
    }
}
</style>
